import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useGetJobsQuery, useRestartJobMutation } from '@/api/client';

const POLLING_INTERVAL_MS = 30000;
const MIN_REFETCH_INDICATOR_MS = 800;
const EMPTY_ARRAY: never[] = [];

export function useJobs(skip: boolean) {
  const { data, isLoading, isFetching, refetch } = useGetJobsQuery(
    { page: 1, size: 100 },
    { skip, pollingInterval: skip ? 0 : POLLING_INTERVAL_MS }
  );

  const jobs = useMemo(() => data?.items ?? EMPTY_ARRAY, [data?.items]);

  const [showRefetching, setShowRefetching] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefetching = isFetching && !isLoading;

  useEffect(() => {
    if (isRefetching) {
      setShowRefetching(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } else if (showRefetching) {
      timeoutRef.current = setTimeout(() => {
        setShowRefetching(false);
      }, MIN_REFETCH_INDICATOR_MS);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isRefetching, showRefetching]);

  const [restartJobMutation] = useRestartJobMutation();

  const restartJob = useCallback(
    async (jobId: string) => {
      await restartJobMutation(jobId);
      refetch();
    },
    [restartJobMutation, refetch]
  );

  return {
    jobs,
    isLoading,
    isRefetching: showRefetching,
    refetch,
    restartJob,
  };
}
